import * as types from './types'
import { IPaginatedRequest } from '@cbgms/api/request'
import {
  GETEmployeeResponse,
  GETEmployeeWorkHoursResponse,
  GETEmployeesResponse,
  GETResponse,
  IConsolidatedLeaveBalance,
  ILeaveBalance,
  ILeaveYearList,
  IResponse,
  IUUIDResponse,
  LoadMechanicsParams,
  POSTEmployeeBody,
  POSTInviteEmployeeBody,
  PUTEmployeeBody,
  PUTEmployeeWorkHoursBody,
  UserStatus
} from '@cbgms/api'

import {
  addLeaveBalanceRequest,
  createNewEmployee,
  deleteEmployeeUser,
  deleteEmployeeWorkHours,
  disableEmployeeAccount,
  enableEmployeeAccount,
  getConsolidatedLeaveBalanceRequest,
  getEmployeeWorkHours,
  getLeaveBalanceRequest,
  getLeaveYearListRequest,
  inviteNewEmployee,
  loadEmployeesList,
  loadExistingEmployeeStatus,
  loadSingleEmployee,
  passwordReset,
  updateExistingEmployee,
  updateExistingEmployeeWorkHours,
  updateLeaveBalanceRequest
} from '@cbgms/api/modules/employees/requests'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { createAsyncApiAction } from '@cbgms/base/redux/create-async-action'

export const loadEmployees = (params: IPaginatedRequest = {}) =>
  AsyncAction<GETEmployeesResponse>({
    type: [types.LOAD_EMPLOYEES, types.LOAD_EMPLOYEES_SUCCESS, types.LOAD_EMPLOYEES_FAILED],
    payload: {
      api: 'gms',
      request: loadEmployeesList(params)
    }
  })

export const loadMechanicsWithAvailability = (params: LoadMechanicsParams) =>
  createAsyncApiAction<GETEmployeesResponse>('gms', {
    method: 'GET',
    url: '/modules/employees/mechanics',
    params: { ...params }
  })

export const loadEmployee = (uuid: string) =>
  AsyncAction<GETEmployeeResponse>({
    type: [types.LOAD_EMPLOYEE, types.LOAD_EMPLOYEE_SUCCESS, types.LOAD_EMPLOYEE_FAILED],
    payload: {
      api: 'gms',
      request: loadSingleEmployee(uuid)
    }
  })

export const createEmployee = (data: POSTEmployeeBody) =>
  AsyncAction<IUUIDResponse>({
    type: [types.CREATE_EMPLOYEE, types.CREATE_EMPLOYEE_SUCCESS, types.CREATE_EMPLOYEE_FAILED],
    payload: {
      api: 'gms',
      request: createNewEmployee(data)
    }
  })

export const updateEmployee = (uuid: string, data: PUTEmployeeBody) => ({
  type: [types.UPDATE_EMPLOYEE, types.UPDATE_EMPLOYEE_SUCCESS, types.UPDATE_EMPLOYEE_FAILED],
  payload: {
    api: 'gms',
    request: updateExistingEmployee(uuid, data)
  }
})

export const getLeaveBalance = (uuid: string, year: number) =>
  AsyncAction<IResponse<ILeaveBalance>>({
    type: [types.ADD_LEAVE_INFO, types.ADD_LEAVE_INFO_SUCCES, types.ADD_LEAVE_INFO_FAILED],
    payload: {
      api: 'gms',
      request: getLeaveBalanceRequest(uuid, year)
    }
  })

export const getLeaveYearList = (uuid: string) =>
  AsyncAction<IResponse<ILeaveYearList>>({
    type: [types.GET_LEAVE_YEAR_LIST, types.GET_LEAVE_YEAR_LIST_SUCCES, types.GET_LEAVE_YEAR_LIST_FAILED],
    payload: {
      api: 'gms',
      request: getLeaveYearListRequest(uuid)
    }
  })

export const getConsolidatedLeaveBalance = (uuid: string, year: number) =>
  AsyncAction<IResponse<IConsolidatedLeaveBalance[]>>({
    type: [types.GET_CONSOLIDATED_LEAVE_BALANCE, types.GET_CONSOLIDATED_LEAVE_BALANCE_SUCCES, types.GET_CONSOLIDATED_LEAVE_BALANCE_FAILED],
    payload: {
      api: 'gms',
      request: getConsolidatedLeaveBalanceRequest(uuid, year)
    }
  })

export const addLeaveBalance = (employeeUUID: string, data: ILeaveBalance) => ({
  type: [types.ADD_LEAVE_INFO, types.ADD_LEAVE_INFO_SUCCES, types.ADD_LEAVE_INFO_FAILED],
  payload: {
    api: 'gms',
    request: addLeaveBalanceRequest(employeeUUID, data)
  }
})

export const updateLeaveBalance = (uuid: string, data: ILeaveBalance) => ({
  type: [types.UPDATE_LEAVE_INFO, types.UPDATE_LEAVE_INFO_SUCCES, types.UPDATE_LEAVE_INFO_FAILED],
  payload: {
    api: 'gms',
    request: updateLeaveBalanceRequest(uuid, data)
  }
})

export const inviteEmployee = (uuid: string, data: POSTInviteEmployeeBody) => ({
  type: [types.INVITE_EMPLOYEE, types.INVITE_EMPLOYEE_SUCCESS, types.INVITE_EMPLOYEE_FAILED],
  payload: {
    api: 'gms',
    request: inviteNewEmployee(uuid, data)
  }
})

export const loadEmployeeStatus = (uuid: string) =>
  AsyncAction<GETResponse<UserStatus>>({
    type: [types.LOAD_EMPLOYEE_STATUS, types.LOAD_EMPLOYEE_STATUS_SUCCESS, types.LOAD_EMPLOYEE_STATUS_FAILED],
    payload: {
      api: 'gms',
      request: loadExistingEmployeeStatus(uuid)
    }
  })

export const triggerPasswordReset = (uuid: string) => ({
  type: [
    types.TRIGGER_EMPLOYEE_PASSWORD_RESET,
    types.TRIGGER_EMPLOYEE_PASSWORD_RESET_SUCCESS,
    types.TRIGGER_EMPLOYEE_PASSWORD_RESET_FAILED
  ],
  payload: {
    api: 'gms',
    request: passwordReset(uuid) // no shop id required here, only for support
  }
})

export const disableUser = (uuid: string) => ({
  type: [types.DISABLE_EMPLOYEE, types.DISABLE_EMPLOYEE_SUCCESS, types.DISABLE_EMPLOYEE_FAILED],
  payload: {
    api: 'gms',
    request: disableEmployeeAccount(uuid)
  }
})

export const enableUser = (uuid: string) => ({
  type: [types.DISABLE_EMPLOYEE, types.DISABLE_EMPLOYEE_SUCCESS, types.DISABLE_EMPLOYEE_FAILED],
  payload: {
    api: 'gms',
    request: enableEmployeeAccount(uuid)
  }
})

export const deleteUser = (uuid: string) => ({
  type: [types.DELETE_EMPLOYEE_USER, types.DELETE_EMPLOYEE_USER_SUCCESS, types.DELETE_EMPLOYEE_USER_FAILED],
  payload: {
    api: 'gms',
    request: deleteEmployeeUser(uuid)
  }
})

export const updateWorkHours = (uuid: string, data: PUTEmployeeWorkHoursBody) => ({
  type: [types.UPDATE_WORK_HOURS, types.UPDATE_WORK_HOURS_SUCCESS, types.UPDATE_WORK_HOURS_FAILED],
  payload: {
    api: 'gms',
    request: updateExistingEmployeeWorkHours(uuid, data)
  }
})

export const getWorkHours = (uuid: string) =>
  AsyncAction<GETEmployeeWorkHoursResponse>({
    type: [types.LOAD_WORK_HOURS, types.LOAD_WORK_HOURS_SUCCESS, types.LOAD_WORK_HOURS_FAILED],
    payload: {
      api: 'gms',
      request: getEmployeeWorkHours(uuid)
    }
  })

export const deleteWorkHours = (uuid: string) => ({
  type: [types.DELETE_WORK_HOURS, types.DELETE_WORK_HOURS_SUCCESS, types.DELETE_WORK_HOURS_FAILED],
  payload: {
    api: 'gms',
    request: deleteEmployeeWorkHours(uuid)
  }
})
